import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import type { AriaContact, ChatMessage, DeviceSession } from "../types";

interface AriaDB extends DBSchema {
  messages: {
    key: string;
    value: ChatMessage;
    indexes: { "by-aria": string };
  };
  contacts: {
    key: string;
    value: AriaContact;
  };
  session: {
    key: "device";
    value: DeviceSession;
  };
}

let dbPromise: Promise<IDBPDatabase<AriaDB>> | null = null;

function getDb() {
  if (!dbPromise) {
    dbPromise = openDB<AriaDB>("aaria-web-ui", 1, {
      upgrade(db) {
        const messages = db.createObjectStore("messages", { keyPath: "id" });
        messages.createIndex("by-aria", "ariaId");
        db.createObjectStore("contacts", { keyPath: "ariaId" });
        db.createObjectStore("session");
      },
    });
  }
  return dbPromise;
}

export async function saveDeviceSession(session: DeviceSession): Promise<void> {
  const db = await getDb();
  await db.put("session", session, "device");
}

export async function loadDeviceSession(): Promise<DeviceSession | null> {
  const db = await getDb();
  const row = await db.get("session", "device");
  if (!row) return null;
  if (Date.parse(row.expiresAt) < Date.now()) {
    await db.delete("session", "device");
    return null;
  }
  return row;
}

export async function clearDeviceSession(): Promise<void> {
  const db = await getDb();
  await db.delete("session", "device");
}

export async function upsertContact(contact: AriaContact): Promise<void> {
  const db = await getDb();
  await db.put("contacts", contact);
}

export async function loadContacts(): Promise<AriaContact[]> {
  const db = await getDb();
  return db.getAll("contacts");
}

export async function saveMessage(msg: ChatMessage): Promise<void> {
  const db = await getDb();
  await db.put("messages", msg);
}

export async function loadMessages(ariaId: string): Promise<ChatMessage[]> {
  const db = await getDb();
  const all = await db.getAllFromIndex("messages", "by-aria", ariaId);
  // Heal legacy rows where assistant reused the user turn id (overwrote user in IDB).
  const healed = all.map((m) => {
    if (m.role === "assistant" && !m.id.startsWith("assistant:")) {
      const turnId = m.turnId ?? m.id;
      return { ...m, id: `assistant:${turnId}`, turnId };
    }
    if (m.role === "user" && !m.turnId) {
      return { ...m, turnId: m.id };
    }
    return m;
  });
  // Re-persist healed assistant ids so future loads don't collide conceptually.
  for (let i = 0; i < all.length; i++) {
    const before = all[i]!;
    const after = healed[i]!;
    if (before.id !== after.id) {
      await db.delete("messages", before.id);
      await db.put("messages", after);
    }
  }
  return healed.sort((a, b) => Date.parse(a.ts) - Date.parse(b.ts));
}

export async function clearMessages(ariaId: string): Promise<void> {
  const db = await getDb();
  const all = await db.getAllFromIndex("messages", "by-aria", ariaId);
  await Promise.all(all.map((m) => db.delete("messages", m.id)));
}
